import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import {
  CallMemberRole,
  ICallPermissionsChanged,
  ICallPermissionsSettings,
  ICallUserPermissionRequestCreated,
  ICallUserPermissionsChanged,
  ICallUserPermissionsRequestChanged,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';

export enum CallsServerErrorCode {
  HasNoPermissionsToPerformAction = 'E_HAS_NO_PERMISSIONS_TO_PERFORM_ACTION',

  TokenExpired = 'E_TOKEN_EXPIRED',
  UserDoesNotExist = 'E_USER_DOES_NOT_EXIST',
  Unauthorized = 'E_UNAUTHORIZED',

  ParticipantLimitReached = 'E_PARTICIPANT_LIMIT_REACHED',
  HasNoPermissionsToCall = 'E_HAS_NO_PERMISSIONS_TO_CALL',
  RecordingIsStartedAlready = 'E_RECORDING_IS_STARTED_ALREADY',
  NoPermissionsToRecordACall = 'E_NO_PERMISSIONS_TO_RECORD_A_CALL',
  ScreenSharingIsRunningByAnotherUser = 'E_SCREEN_SHARING_IS_RUNNING_BY_ANOTHER_USER',
  ChannelClosed = 'E_CHANNEL_CLOSED',
  OnlyWebPlatformSupportedManyBroadcasters = 'E_ONLY_WEB_PLATFORM_SUPPORTED_MANY_BROADCASTERS',
  TooManyInvitedMembersInShortTime = 'E_TOO_MANY_INVITED_MEMBERS_IN_SHORT_TIME',
  CallAlreadyExist = 'E_CALL_ALREADY_EXIST',
  PermissionAlreadySentError = 'E_PERMISSION_REQUEST_ALREADY_SENT',
  AttemptsExceeded = 'E_ATTEMPTS_EXCEEDED',
  ParticipantsPerRoleExceeded = 'E_PARTICIPANTS_PER_ROLE_EXCEEDED',
  BadData = 'E_BAD_DATA',
}

export interface IBooleanResponse {
  result: boolean;
}

export enum DeviceInfoPlatform {
  Android = 'android',
  Huawei = 'huawei',
  Web = 'web',
  Ios = 'ios',
  Safari = 'safari',
  Desktop = 'desktop',
}

export interface ICallMemberDevice {
  deviceId: ProviderUid;
  providerUserUid: ProviderUid;
  shareScreenUid: ProviderUid;
  status: CallMemberStatus;
}

export interface ICallMember {
  providerUserUid: ProviderUid;
  avatarUrl?: string;
  hasPermissionRequest: boolean;
  isHandRaised: boolean;
  handRaisedAt: number | null;
  platform?: DeviceInfoPlatform;
  shareScreenUid: ProviderUid;
  status: CallMemberStatus;
  userId: string;
  username: string;
  isAdmin?: boolean;
  device: ICallMemberDevice | null;
}

export enum ParticipantsControlType {
  Disabled = 'DISABLED',
  WithMaxNumber = 'WITH_MAX_NUMBER',
  TotalNumber = 'TOTAL_NUMBER',
}

export enum ParticipantGridDisplayMode {
  Admin = 'ADMIN',
  All = 'ALL',
}

export enum PreviewDisplayMode {
  DefaultPreview = 'DEFAULT_PREVIEW',
  WebinarPreview = 'WEBINAR_PREVIEW',
}

export enum PermissionsSectionDisplayMode {
  CustomPermissions = 'CUSTOM_PERMISSIONS',
  WebinarSpeakers = 'WEBINAR_SPEAKERS',
}

export enum MemberListDisplayMode {
  Default = 'DEFAULT',
  NameWithRole = 'NAME_WITH_ROLE',
}

export interface ICallUIFeatures {
  participantsControlType: ParticipantsControlType;
  raiseHandControlEnabled: boolean;
  endCallForAllEnabled: boolean;
  permissionsAndAccessControlEnabled: boolean;
  excludeParticipantsFromUserSearch: boolean;
  showParticipantsNumberRestrictionBanner: boolean;
  showCallWillEndSoonBanner: boolean;
  showParticipantsLimitWillReachSoonBanner: boolean;
  showParticipantsRestrictedLimitReachedBanner: boolean;
  showDurationLimitReachedBanner: boolean;
  showControlMic: boolean;
  showControlCamera: boolean;
  showControlScreenSharing: boolean;
  changeParticipantRole: boolean;
  participantGridDisplayMode: ParticipantGridDisplayMode;
  showCopyLinkOnPanel: boolean;
  showCopyLinkInMoreMenu: boolean;
  showControlBackground: boolean;
  showControlRecording: boolean;
  showControlInviteMembers: boolean;
  isActiveControlParticipantListWeb: boolean;
  previewDisplayMode: PreviewDisplayMode;
  permissionsSectionDisplayMode: PermissionsSectionDisplayMode;
  memberListDisplayMode: MemberListDisplayMode;
}

export interface IVideoQualityPreset {
  maxBitrate: number;
  maxFramerate: number;
  resolution: CallVideoResolution;
}

export interface IVideoQualitySettings {
  low: IVideoQualityPreset;
  medium: IVideoQualityPreset;
  high: IVideoQualityPreset;
}

export interface ICallInfo {
  audioVideoUid: ProviderUid;
  shareScreenUid: ProviderUid;
  channelId: string;
  currentTs: string;
  groupId?: string;
  initiatorId: string;
  isAdmin: boolean;
  maxBroadcasters: number;
  members: ICallMember[];
  permissions?: ICallPermissionsSettings;
  recordingId?: string;
  shareScreenToken: string;
  audioVideoToken: string;
  callVisibleDuration: number | null;
  callName: string;
  waitingRoomEnabled: boolean;
  guestsAllowed: boolean;
  deviceId: string;
  uiFeatures: ICallUIFeatures;

  approxDurationMs: number;
  maxParticipants: number;
  maxParticipantsReminder?: number;
  maxDuration?: number;
  maxDurationReminder?: number;
  videoQualitySettings: IVideoQualitySettings | null;
}

export interface ICallCreatedInfo {
  channelId: string;
}

export interface ICallCreateProps {
  groupId: string | null;
  providerType: CallProviderType;
}

export interface IIncomingCall {
  channelId: string;
  callVisibleDuration: number;
  groupId: string;
  callerId: string;
  callerName: string;
  callerAvatarUrl: string;
  callProvider: CallProviderType;
}

export interface IGroupCallChanged {
  channelId: string;
  status: CallStatus;
  callProvider: CallProviderType;
  groupId: string;
}

export interface IStatusCallChanged {
  status: CallStatus;
  waitingRoomEnabled: boolean;
  guestsAllowed: boolean;
  uiFeatures?: ICallUIFeatures;
  statusCallFinishReason?: StatusCallFinishReason;
}

export interface ICallMemberChanged {
  memberId: string;
  status: CallMemberStatus;
  deviceId: ProviderUid;
}

export interface ICallRecordingStarted {
  recordingId: string;
  recorderId: string;
}

export interface ICallRecordingStopped {
  recordingId: string;
}

export interface ICallMemberHandAction {
  memberId: string;
}

export interface ICallMemberForceMuted {
  initiatorId: string;
  targetMemberId: string;
  deviceId: ProviderUid | null;
}

export interface ICallMemberForceFinished {
  initiatorId: string;
  targetMemberId: string;
  platforms?: DeviceInfoPlatform[];
  deviceId: ProviderUid | null;
}

export interface ICallMemberWasRemoved {
  memberId: string;
  initiatorId?: string;
}

export interface ICallRecordingEvent {
  channel: string;
  recordingId: string;
  recorderId: string;
  event: string;
}

export interface ICallMemberDisplayData {
  userName: string;
  avatarUrl?: string;
}

export interface IWaitingRoomMember {
  memberId: string;
  displayData: ICallMemberDisplayData;
  status: WaitingRoomMemberChangedStatus;
}

export interface IWaitingRoomChanged {
  requested: IWaitingRoomMember[];
}

export enum CallStatus {
  Initiated = 'INITIATED',
  Started = 'STARTED',
  Finished = 'FINISHED',
}

export enum CallProviderType {
  Agora = 'AGORA',
  Openvidu = 'OPENVIDU',
  Livekit = 'LIVEKIT',
}

export enum RecordingType {
  Audio = 'AUDIO',
  AudioVideo = 'AUDIO_VIDEO',
}

export enum CallAction {
  ForceFinish = 'FORCE_FINISH',
  ForceMute = 'FORCE_MUTE',
  RaiseHand = 'RAISE_HAND',
  PutHand = 'PUT_HAND',
  UnmuteAccess = 'UNMUTE_ACCESS',
  ForceCallback = 'FORCE_CALLBACK',
}

export enum CallEventName {
  CallsActiveCallChanged = 'calls.active.call-changed',
  CallsActiveCallAction = 'calls.active.call-action',
  CallsActiveUserChanged = 'calls.active.user-changed',
  CallsActiveRecordingStarted = 'calls.active.recording-started',
  CallsActiveRecordingStopped = 'calls.active.recording-stopped',
  CallsActivePermissionsChanged = 'calls.active.permissions-changed',
  CallsActiveUserPermissionsChanged = 'calls.active.user-permissions-changed',
  CallsActivePermissionRequestCreated = 'calls.active.permission-request-created',
  CallsActivePermissionRequestChanged = 'calls.active.permission-request-changed',
  CallsStatusChanged = 'calls.status-changed',
}

export enum CallMemberStatus {
  OnCall = 'ON_CALL',
  NotOnCall = 'NON_ON_CALL',
  Declined = 'DECLINED',
  Left = 'LEFT',
}

export enum CallStreamType {
  Audio = 'AUDIO',
  Video = 'VIDEO',
}

export enum CallMemberEventStatus {
  Declined = 'DECLINED',
  Finished = 'FINISHED',
  Initiated = 'INITIATED',
  Joined = 'JOINED',
  Added = 'ADDED',
  Removed = 'REMOVED',
}

export enum FinishCallReason {
  Declined = 'DECLINED',
  FinishAll = 'FINISH_ALL',
  Left = 'LEFT',
}

export enum WaitingRoomMemberChangedStatus {
  Initiated = 'INITIATED',
  Approved = 'APPROVED',
  Rejected = 'REJECTED',
  Requested = 'REQUESTED',
}

export enum WaitingRoomRequestStatus {
  Approve = 'APPROVE',
  Reject = 'REJECT',
}

export enum WaitingRoomAccessStatus {
  AskToJoin = 'ASK_TO_JOIN',
  Cancel = 'CANCEL',
}

export interface ICallListenerParams {
  channelId: string;
  isUnauthorizedCall?: boolean;
  onUserCallData?(data: ICallMemberChanged): void;
  onActiveCallData?(data: IGroupCallChanged): void;
  onPermissionsData?(data: ICallPermissionsChanged): any;
  onMemberPermissionsData?(data: ICallUserPermissionsChanged): any;
  onCreatePermissionsRequestData?(data: ICallUserPermissionRequestCreated): any;
  onChangePermissionRequestData?(data: ICallUserPermissionsRequestChanged): any;
  onStatusCallChanged?(data: IStatusCallChanged): void;
  onRecordingStarted?(data: ICallRecordingStarted): void;
  onRecordingStopped?(data: ICallRecordingStopped): void;
  onMemberForceMuted?(data: ICallMemberForceMuted): void;
  onMemberForceFinished?(data: ICallMemberForceFinished): void;
  onMemberWasRemoved?(data: ICallMemberWasRemoved): void;
  onMemberWasAdded?(data: ICallMember): void;
  onWaitingRoomChanged?(data: IWaitingRoomChanged): void;
  onWaitingRoomMemberChanged?(data: IWaitingRoomMember): void;
  onMemberRoleChanged?(data: IMemberRoleChanged): void;
  onError?(data: { target: EventSource }): void;
}

export interface IWaitingRoomListenerParams {
  onWaitingRoomChanged?(data: IWaitingRoomChanged): void;
  onWaitingRoomMemberChanged?(data: IWaitingRoomMember): void;
  onStatusCallChanged?(data: IStatusCallChanged): void;
}

export type TInitWaitingRoomCallListenerFunc = (
  params: IWaitingRoomListenerParams
) => (() => void) | null;

export type TInitCallListenerFunc = (params: ICallListenerParams) => (() => void) | null;

export interface IUpdateWaitingRoomForMemberParams {
  channelId: string;
  memberId: string;
  status: WaitingRoomRequestStatus;
}

export interface IUpdateWaitingRoomForAllParams {
  channelId: string;
  status: WaitingRoomRequestStatus;
}

export interface IRequestWaitingRoomAccessParams {
  channelId: string;
  status: WaitingRoomAccessStatus;
}

export interface ICreateRoomResult {
  link: string;
  channelId: string;
}

export interface IMemberRoleChanged {
  userId: string;
  role: CallMemberRole;
}

export enum CallVideoResolution {
  P360 = 'h360',
  P480 = 'h480',
  P720 = 'h720',
  P1080 = 'h1080',
}

export enum StatusCallFinishReason {
  DurationLimitReached = 'DurationLimitReached',
}
